import { useState, useCallback } from 'react';

import {
	BsPlus,
	BsTrash,
	BsStarFill,
	BsStar,
	BsPencil,
	BsGripVertical,
	BsTrophyFill,
	BsTrophy,
	BsXCircleFill,
	BsXCircle,
	BsCheck2,
} from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';

import NewPipelineModal from '../../../components/settings/NewPipelineModal/NewPipelineModal';
import {
	useUpdatePipeline,
	useDeletePipeline,
	useCreateStage,
	useUpdateStage,
	useDeleteStage,
	useReorderStages,
} from '../../../models/pipelines/mutations';
import { usePipelines } from '../../../models/pipelines/queries';

const PipelinesSettingsTab = () => {
	const { data: pipelines = [], isLoading } = usePipelines();
	const updatePipeline = useUpdatePipeline();
	const deletePipeline = useDeletePipeline();
	const createStage = useCreateStage();
	const updateStage = useUpdateStage();
	const deleteStage = useDeleteStage();
	const reorderStages = useReorderStages();
	const showError = useErrorStore((s) => s.showError);

	const [deletingId, setDeletingId] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [showNewPipeline, setShowNewPipeline] = useState(false);
	const [editingPipelineId, setEditingPipelineId] = useState(null);

	// Drag state (native HTML5 D&D like Contacts page)
	const [dragIdx, setDragIdx] = useState(null);
	const [localStages, setLocalStages] = useState([]);
	const [localPipelineName, setLocalPipelineName] = useState('');
	const [originalPipelineName, setOriginalPipelineName] = useState('');
	const [originalStageNames, setOriginalStageNames] = useState({});

	// New-stage inline form
	const [newStageName, setNewStageName] = useState('');

	// Stage delete confirm
	const [stageDeleteTarget, setStageDeleteTarget] = useState(null);

	const startEditing = (pipeline) => {
		const stages = pipeline.stages ?? [];
		setEditingPipelineId(pipeline.id);
		setLocalStages([...stages]);
		setLocalPipelineName(pipeline.name);
		setOriginalPipelineName(pipeline.name);
		setOriginalStageNames(Object.fromEntries(stages.map((s) => [s.id, s.name])));
		setNewStageName('');
	};

	const resetEditingState = () => {
		setEditingPipelineId(null);
		setLocalStages([]);
		setLocalPipelineName('');
		setOriginalPipelineName('');
		setOriginalStageNames({});
		setNewStageName('');
		setDragIdx(null);
	};

	const handleFinishEditing = async () => {
		if (!editingPipelineId) return;

		const trimmedPipelineName = localPipelineName.trim();
		const updates = [];

		if (trimmedPipelineName && trimmedPipelineName !== originalPipelineName) {
			updates.push(
				updatePipeline
					.mutateAsync({ id: editingPipelineId, payload: { name: trimmedPipelineName } })
					.catch((err) => showError({ error: err, title: 'Pipeline-Name konnte nicht gespeichert werden' })),
			);
		}

		localStages.forEach((s) => {
			const trimmed = s.name.trim();
			const original = originalStageNames[s.id];
			// Only update existing stages (newly created stages were already persisted with their final name)
			if (original !== undefined && trimmed && trimmed !== original) {
				updates.push(
					updateStage
						.mutateAsync({
							pipelineId: editingPipelineId,
							stageId: s.id,
							payload: { name: trimmed },
						})
						.catch((err) =>
							showError({ error: err, title: `Stage "${original}" konnte nicht gespeichert werden` }),
						),
				);
			}
		});

		await Promise.all(updates);
		resetEditingState();
	};

	const handleStageNameChange = (stageId, value) => {
		setLocalStages((prev) => prev.map((s) => (s.id === stageId ? { ...s, name: value } : s)));
	};

	// Pipeline actions
	const handleSetDefault = async (pipeline) => {
		try {
			await updatePipeline.mutateAsync({ id: pipeline.id, payload: { isDefault: true } });
		} catch (err) {
			showError({ error: err, title: 'Standard konnte nicht gesetzt werden' });
		}
	};

	const handleDeletePipeline = async () => {
		if (!deleteTarget) return;
		setDeletingId(deleteTarget.id);
		try {
			await deletePipeline.mutateAsync(deleteTarget.id);
			if (editingPipelineId === deleteTarget.id) resetEditingState();
		} catch (err) {
			showError({ error: err, title: 'Pipeline konnte nicht gelöscht werden' });
		} finally {
			setDeletingId(null);
			setDeleteTarget(null);
		}
	};

	// Drag handlers (native HTML5, same pattern as CrmCompaniesPage)
	const handleDragStart = useCallback((idx) => setDragIdx(idx), []);

	const handleDragOver = useCallback(
		(e, idx) => {
			e.preventDefault();
			if (dragIdx === null || dragIdx === idx) return;
			setLocalStages((prev) => {
				const next = [...prev];
				const [moved] = next.splice(dragIdx, 1);
				next.splice(idx, 0, moved);
				return next;
			});
			setDragIdx(idx);
		},
		[dragIdx],
	);

	const handleDragEnd = useCallback(async () => {
		if (dragIdx === null) return;
		setDragIdx(null);
		if (!editingPipelineId) return;

		const stageOrders = localStages.map((s, i) => ({ id: s.id, stageOrder: i }));
		try {
			await reorderStages.mutateAsync({ pipelineId: editingPipelineId, stageOrders });
		} catch (err) {
			showError({ error: err, title: 'Reihenfolge konnte nicht gespeichert werden' });
		}
	}, [dragIdx, editingPipelineId, localStages, reorderStages, showError]);

	// Stage type toggle
	const handleToggleStageType = async (stage, targetType) => {
		if (!editingPipelineId) return;
		const newType = stage.stageType === targetType ? 'active' : targetType;

		// Optimistically update local state first — ensure only one won/lost at a time
		setLocalStages((prev) =>
			prev.map((s) => {
				if (s.id === stage.id) return { ...s, stageType: newType };
				// Reset any other stage that had the same targetType back to active
				if (newType !== 'active' && s.stageType === targetType) return { ...s, stageType: 'active' };
				return s;
			}),
		);

		try {
			await updateStage.mutateAsync({
				pipelineId: editingPipelineId,
				stageId: stage.id,
				payload: { stageType: newType },
			});
		} catch (err) {
			// Revert on error — restore from server data
			const pipeline = pipelines.find((p) => p.id === editingPipelineId);
			if (pipeline) setLocalStages([...(pipeline.stages ?? [])]);
			showError({ error: err, title: 'Stage-Typ konnte nicht geändert werden' });
		}
	};

	// Add stage
	const handleAddStage = async (e) => {
		e.preventDefault();
		const name = newStageName.trim();
		if (!name || !editingPipelineId) return;

		try {
			const { stage: created } = await createStage.mutateAsync({
				pipelineId: editingPipelineId,
				payload: { name, stageType: 'active' },
			});
			setLocalStages((prev) => [...prev, created]);
			setOriginalStageNames((prev) => ({ ...prev, [created.id]: created.name }));
			setNewStageName('');
		} catch (err) {
			showError({ error: err, title: 'Stage konnte nicht erstellt werden' });
		}
	};

	// Delete stage
	const handleDeleteStage = async () => {
		if (!stageDeleteTarget || !editingPipelineId) return;
		try {
			await deleteStage.mutateAsync({
				pipelineId: editingPipelineId,
				stageId: stageDeleteTarget.id,
			});
			setLocalStages((prev) => prev.filter((s) => s.id !== stageDeleteTarget.id));
		} catch (err) {
			showError({ error: err, title: 'Stage konnte nicht gelöscht werden' });
		} finally {
			setStageDeleteTarget(null);
		}
	};

	if (isLoading) return <p className="text-bgBase-800">Pipelines werden geladen …</p>;

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h2 className="text-sm font-semibold text-text">Pipelines</h2>
					<p className="text-xs text-bgBase-800">Verwalten Sie Ihre Vertriebspipelines und deren Stages.</p>
				</div>
				<Button variant="outline" size="sm" className="shrink-0" onClick={() => setShowNewPipeline(true)}>
					<BsPlus className="w-4 h-4 mr-1" />
					Neue Pipeline
				</Button>
			</div>

			{/* Pipeline list */}
			<div className="space-y-3">
				{pipelines.length === 0 && (
					<div className="rounded-card border-2 border-dashed border-bgBase-500 p-8 text-center text-sm text-bgBase-800">
						Noch keine Pipelines vorhanden.
					</div>
				)}

				{pipelines.map((p) => {
					const isEditing = editingPipelineId === p.id;
					const stages = isEditing ? localStages : (p.stages ?? []);

					return (
						<Card
							key={p.id}
							className={`p-0 transition-colors ${isEditing ? 'border-primary-200 ring-1 ring-primary-100' : ''}`}
						>
							<div className="flex items-center justify-between p-5 pb-3">
								<div className="flex items-center gap-2 flex-1 min-w-0">
									{isEditing ? (
										<input
											type="text"
											value={localPipelineName}
											onChange={(e) => setLocalPipelineName(e.target.value)}
											placeholder="Pipeline-Name"
											className="font-semibold text-text bg-transparent border-b border-primary-300 focus:outline-none focus:border-primary-500 px-0.5 py-0.5 min-w-0 flex-shrink"
										/>
									) : (
										<h3 className="font-semibold text-text">{p.name}</h3>
									)}
									{p.isDefault ? (
										<span className="inline-flex items-center gap-1 text-[11px] font-medium text-caution-700 bg-caution-200 px-2 py-0.5 rounded-full">
											<BsStarFill className="w-2.5 h-2.5" />
											Standard
										</span>
									) : (
										<button
											onClick={() => handleSetDefault(p)}
											disabled={updatePipeline.isPending}
											className="inline-flex items-center gap-1 text-[11px] text-bgBase-800 hover:text-caution-700"
										>
											<BsStar className="w-2.5 h-2.5" />
											Als Standard setzen
										</button>
									)}
								</div>

								<div className="flex items-center gap-2">
									{isEditing ? (
										<button
											onClick={handleFinishEditing}
											disabled={updatePipeline.isPending || updateStage.isPending}
											className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 font-medium disabled:opacity-50"
										>
											<BsCheck2 className="w-3.5 h-3.5" />
											Fertig
										</button>
									) : (
										<button
											onClick={() => startEditing(p)}
											className="text-xs text-bgBase-800 hover:text-text flex items-center gap-1"
										>
											<BsPencil className="w-3 h-3" />
											Bearbeiten
										</button>
									)}
									<button
										onClick={() => setDeleteTarget(p)}
										disabled={deletingId === p.id}
										className="text-xs text-warning-700 hover:text-warning-700 flex items-center gap-1"
									>
										<BsTrash className="w-3 h-3" />
										Löschen
									</button>
								</div>
							</div>

							{p.description && <p className="text-xs text-bgBase-800 px-5 mb-3">{p.description}</p>}

							{!isEditing && (
								<div className="flex flex-wrap gap-1.5 px-5 pb-5">
									{stages.map((s) => (
										<span
											key={s.id}
											className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-bgBase-400 bg-bgBase-300"
										>
											<span className="w-2 h-2 rounded-full bg-primary-500" />
											{s.name}
											{s.stageType === 'won' && (
												<BsTrophyFill className="w-2.5 h-2.5 text-success-500" />
											)}
											{s.stageType === 'lost' && (
												<BsXCircleFill className="w-2.5 h-2.5 text-warning-500" />
											)}
										</span>
									))}
								</div>
							)}

							{/* Stages — edit mode */}
							{isEditing && (
								<div className="px-5 pb-5">
									<div className="rounded-card border border-bgBase-500 bg-bgBase-300 divide-y divide-bgBase-500 mb-3">
										{stages.length === 0 && (
											<div className="px-4 py-6 text-center text-xs text-bgBase-800">
												Keine Stages vorhanden. Fügen Sie unten eine hinzu.
											</div>
										)}

										{stages.map((s, idx) => (
											<div
												key={s.id}
												draggable
												onDragStart={() => handleDragStart(idx)}
												onDragOver={(e) => handleDragOver(e, idx)}
												onDragEnd={handleDragEnd}
												className={`flex items-center gap-2.5 px-3.5 py-2.5 cursor-grab active:cursor-grabbing transition-colors ${
													dragIdx === idx
														? 'bg-primary-100 border-y border-primary-200 -my-px'
														: 'hover:bg-white'
												}`}
											>
												<BsGripVertical className="w-3 h-3 text-bgBase-800 flex-shrink-0" />

												<span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-primary-500" />

												<input
													type="text"
													value={s.name}
													onChange={(e) => handleStageNameChange(s.id, e.target.value)}
													onMouseDown={(e) => e.stopPropagation()}
													draggable={false}
													className="flex-1 text-[13px] font-medium text-text bg-transparent border border-transparent hover:border-bgBase-500 focus:border-primary-300 focus:bg-white focus:outline-none rounded-control px-1.5 py-0.5 cursor-text"
												/>

												<div className="flex items-center gap-1">
													<button
														onClick={() => handleToggleStageType(s, 'won')}
														title={
															s.stageType === 'won'
																? 'Gewonnen-Status entfernen'
																: 'Als "Gewonnen" markieren'
														}
														className={`w-6 h-6 rounded-control flex items-center justify-center transition-[color,background-color,filter] ${
															s.stageType === 'won'
																? 'text-success-700 bg-success-200 hover:brightness-95'
																: 'text-bgBase-800 hover:text-success-700 hover:bg-success-200'
														}`}
													>
														{s.stageType === 'won' ? (
															<BsTrophyFill className="w-3 h-3" />
														) : (
															<BsTrophy className="w-3 h-3" />
														)}
													</button>

													<button
														onClick={() => handleToggleStageType(s, 'lost')}
														title={
															s.stageType === 'lost'
																? 'Verloren-Status entfernen'
																: 'Als "Verloren" markieren'
														}
														className={`w-6 h-6 rounded-control flex items-center justify-center transition-[color,background-color,filter] ${
															s.stageType === 'lost'
																? 'text-warning-700 bg-warning-200 hover:brightness-95'
																: 'text-bgBase-800 hover:text-warning-700 hover:bg-warning-200'
														}`}
													>
														{s.stageType === 'lost' ? (
															<BsXCircleFill className="w-3 h-3" />
														) : (
															<BsXCircle className="w-3 h-3" />
														)}
													</button>

													<IconButton
														icon={BsTrash}
														label="Stage löschen"
														tone="danger"
														onClick={() => setStageDeleteTarget(s)}
														className="h-6 w-6"
													/>
												</div>
											</div>
										))}
									</div>

									{/* Add stage inline */}
									<form onSubmit={handleAddStage} className="flex items-center gap-2">
										<TextInput
											value={newStageName}
											onChange={(e) => setNewStageName(e.target.value)}
											placeholder="Neue Stage hinzufügen …"
											className="flex-1"
										/>
										<Button
											type="submit"
											variant="outline"
											size="sm"
											disabled={!newStageName.trim() || createStage.isPending}
										>
											<BsPlus className="w-4 h-4 mr-0.5" />
											Hinzufügen
										</Button>
									</form>
								</div>
							)}
						</Card>
					);
				})}
			</div>

			{/* New pipeline modal */}
			{showNewPipeline && <NewPipelineModal isOpen={showNewPipeline} onClose={() => setShowNewPipeline(false)} />}

			{/* Pipeline delete confirm */}
			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDeletePipeline}
				title="Pipeline in den Papierkorb verschieben"
				message={
					<>
						Pipeline <strong>&quot;{deleteTarget?.name}&quot;</strong> und alle zugehörigen Deals werden in
						den Papierkorb verschoben und können 30 Tage lang wiederhergestellt werden.
					</>
				}
				confirmText="In Papierkorb"
				isLoading={Boolean(deletingId)}
			/>

			{/* Stage delete confirm */}
			<ConfirmModal
				isOpen={Boolean(stageDeleteTarget)}
				onClose={() => setStageDeleteTarget(null)}
				onConfirm={handleDeleteStage}
				title="Stage löschen"
				message={
					<>
						Stage <strong>&quot;{stageDeleteTarget?.name}&quot;</strong> wirklich löschen? Deals in dieser
						Stage müssen manuell verschoben werden.
					</>
				}
				isLoading={deleteStage.isPending}
			/>
		</div>
	);
};

export default PipelinesSettingsTab;
